/**
 * Tahiti timezone utilities (UTC-10).
 * All week calculations use ISO 8601: Monday = start of week.
 */

/** Get a Date object representing "now" in Tahiti (shifted UTC). Use getUTC* methods on it. */
export function getTahitiNow() {
  return new Date(Date.now() - 10 * 3600 * 1000);
}

/** Get today's date in Tahiti as YYYY-MM-DD. */
export function getTahitiDate() {
  return getTahitiNow().toISOString().slice(0, 10);
}

/**
 * Get the Monday (ISO week start) for a given week offset from the current Tahiti week.
 * offset=0 → current week's Monday, offset=-1 → last week's Monday, offset=1 → next week's Monday.
 * Returns YYYY-MM-DD string (always a Monday).
 */
export function getWeekStartTahiti(offset = 0) {
  const now = getTahitiNow();
  // Shift by offset weeks
  const d = new Date(now);
  d.setUTCDate(d.getUTCDate() + offset * 7);
  // getUTCDay: 0=Sun, 1=Mon, ..., 6=Sat
  // We need Monday. daysSinceMonday: Mon=0, Tue=1, ..., Sun=6
  const jsDay = d.getUTCDay(); // 0=Sun
  const daysSinceMonday = jsDay === 0 ? 6 : jsDay - 1;
  d.setUTCDate(d.getUTCDate() - daysSinceMonday);
  return d.toISOString().slice(0, 10);
}

/**
 * Format a week range as "lundi X [mois] — dimanche Y [mois]" (French, Tahiti).
 * @param {string} weekStart - YYYY-MM-DD (must be a Monday)
 * @returns {string} e.g. "lun. 6 juil. — dim. 12 juil."
 */
export function formatWeekRange(weekStart) {
  const monday = new Date(weekStart + 'T12:00:00Z');
  const sunday = new Date(monday);
  sunday.setUTCDate(sunday.getUTCDate() + 6);
  const opts = { day: 'numeric', month: 'short', timeZone: 'UTC' };
  const monStr = monday.toLocaleDateString('fr-FR', opts);
  const sunStr = sunday.toLocaleDateString('fr-FR', opts);
  return `lun. ${monStr} — dim. ${sunStr}`;
}

/**
 * Format a week range as full text for display.
 * @param {string} weekStart - YYYY-MM-DD (must be a Monday)
 * @returns {string} e.g. "du lundi 6 au dimanche 12 juillet"
 */
export function formatWeekRangeFull(weekStart) {
  const monday = new Date(weekStart + 'T12:00:00Z');
  const sunday = new Date(monday);
  sunday.setUTCDate(sunday.getUTCDate() + 6);
  const monDay = monday.getUTCDate();
  const sunDay = sunday.getUTCDate();
  const monMonth = monday.toLocaleDateString('fr-FR', { month: 'long', timeZone: 'UTC' });
  const sunMonth = sunday.toLocaleDateString('fr-FR', { month: 'long', timeZone: 'UTC' });
  if (monMonth === sunMonth) {
    return `du lundi ${monDay} au dimanche ${sunDay} ${sunMonth}`;
  }
  return `du lundi ${monDay} ${monMonth} au dimanche ${sunDay} ${sunMonth}`;
}

/**
 * Get the Tahiti day of week (0=Monday, 6=Sunday) for today.
 */
export function getTahitiDayOfWeek() {
  const now = getTahitiNow();
  const jsDay = now.getUTCDay(); // 0=Sun
  return jsDay === 0 ? 6 : jsDay - 1; // 0=Mon, 6=Sun
}
